import { Component, Build, Prop, Element, h, Watch, State } from '@stencil/core';

import * as am5 from '@amcharts/amcharts5/index';
import { addAxisLabelsToXYChart } from '../chartUtils';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { parseValueToNumber } from '../chartUtils';

const isNumberList = (obj: any): boolean => {
  // Check if obj is an array
  if (!Array.isArray(obj)) {
    return false;
  }

  // Check if every element in the array is a number
  return obj.every(element => typeof element === 'number');
};

const generateMockData = (n: number, mean: number, stdDev: number): number[] => {
  const randomNumbers: number[] = [];

  for (let i = 0; i < n / 2; i++) {
    const u1 = Math.random();
    const u2 = Math.random();
    const z0 = Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);
    const z1 = Math.sqrt(-2.0 * Math.log(u1)) * Math.sin(2.0 * Math.PI * u2);

    randomNumbers.push(mean + z0 * stdDev);
    randomNumbers.push(mean + z1 * stdDev);
  }

  // If n is odd, remove the last element to get exactly n elements
  if (randomNumbers.length > n) {
    randomNumbers.pop();
  }

  return randomNumbers;
};

function getHistogramData(source: number[]): { from: number; to: number; count: number }[] {
  const maxCols = 10;
  // Init
  let data = [];
  let min = Math.round(Math.min.apply(null, source));
  let max = Math.max.apply(null, source);
  let range = max - min;
  let step = parseFloat((range / maxCols).toFixed(1));

  // Create items
  for (let i = 0; i < maxCols; i++) {
    let from = min + i * step;
    let to = min + (i + 1) * step;
    data.push({
      from: from,
      to: to,
      count: 0,
    });
  }

  // Calculate range of the values
  for (let i = 0; i < source.length; i++) {
    let value = source[i];
    let item = data.find(function (el) {
      return value >= el.from && value <= el.to;
    });
    if (item) item.count++;
  }
  return data;
}

@Component({
  tag: 'udp-distribution-chart',
  styleUrl: 'udp-distribution-chart.css',
  shadow: true,
})
export class UdpDistributionChart {
  @Element() el: HTMLElement;
  @Prop() data: any[] = Build.isDev ? generateMockData(1000, 100, 10) : undefined;
  @Prop() valueField: string;
  @Prop() xAxisLabel: string;
  @Prop() title: string = Build.isDev ? 'Test header' : undefined;
  @Prop() subheader: string = Build.isDev ? 'Test SubHeader' : undefined;
  @Prop() size: 'small' | 'medium' | 'large' = 'medium';
  @Prop() kpis: { value: string; label: string }[] = Build.isDev
    ? [
        { value: Math.round(Math.random() * 2000).toString(), label: 'YTD' },
        { value: Math.round(Math.random() * 2000).toString(), label: 'YTD' },
        { value: Math.round(Math.random() * 2000).toString(), label: 'YTD' },
      ]
    : undefined;
  @Prop() xLabel: string = Build.isDev ? 'Test X Label' : undefined;
  @Prop() yLabel: string = Build.isDev ? 'Test Y Label' : undefined;
  @Prop() customWidth?: number; // Rather then setting a size you can set a custom width
  @Prop() customHeight?: number; // Rather then setting a size you can set a custom height
  @Prop() customCardWidth?: number; // Rather then setting a size you can set a custom card width
  @Prop() customCardStyle?: {[key: string]: string} = {};
  @Prop() kpisPosition: 'left' | 'top' = 'left';
  @Prop() hideAdornment: boolean = false;

  private chartId = 'udp-histogram';
  private chartRoot: am5.Root;
  @State() transformedData: { from: number; to: number; count: number }[];

  @Watch('valueField')
  @Watch('data')
  @Watch('xLabel')
  @Watch('yLabel')
  transformData() {
    if (!this.data || this.data.length === 0 || (this.valueField === null && !this.data.every(item => typeof item === 'number'))) return;

    if (!this.data || this.data.length === 0) return;
    let isolatedData;
    if (this.valueField) isolatedData = this.data.map(row => parseValueToNumber(row[this.valueField]));
    else isolatedData = this.data;
    if (!isNumberList(isolatedData)) return;

    this.transformedData = getHistogramData(isolatedData);
  }

  @Watch('transformedData')
  async renderChart() {
    // sometimes the element cannot be found right away so loop until it loads
    if (this.el.shadowRoot.getElementById(this.chartId) == null) {
      await setTimeout(this.renderChart, 50);
    }
    if (this.chartRoot) {
      this.chartRoot.dispose();
    }
    this.chartRoot = am5.Root.new(this.el.shadowRoot.getElementById(this.chartId));

    this.chartRoot.setThemes([am5themes_Animated.new(this.chartRoot)]);

    // Create chart
    // https://www.amcharts.com/docs/v5/charts/xy-chart/
    var chart = this.chartRoot.container.children.push(
      am5xy.XYChart.new(this.chartRoot, {
        panX: false,
        panY: false,
        wheelX: 'panX',
        wheelY: 'zoomX',
        layout: this.chartRoot.verticalLayout,
      }),
    );

    // Add legend
    // https://www.amcharts.com/docs/v5/charts/xy-chart/legend-xy-series/
    // var legend = chart.children.push(
    //   am5.Legend.new(this.chartRoot, {
    //     centerX: am5.p50,
    //     x: am5.p50
    //   })
    // );

    // Create axes
    // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
    var xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(this.chartRoot, {
        categoryField: 'from',
        renderer: am5xy.AxisRendererX.new(this.chartRoot, {
          minGridDistance: 30,
        }),
        tooltip: am5.Tooltip.new(this.chartRoot, {}),
      }),
    );

    xAxis.data.setAll(this.transformedData);
    console.log(this.transformedData);
    var yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(this.chartRoot, {
        maxPrecision: 0,
        renderer: am5xy.AxisRendererY.new(this.chartRoot, {}),
      }),
    );

    addAxisLabelsToXYChart(this.chartRoot, chart, this.xLabel, this.yLabel);

    // Add series
    // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
    var series = chart.series.push(
      am5xy.ColumnSeries.new(this.chartRoot, {
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'count',
        categoryXField: 'from',
      }),
    );

    series.columns.template.setAll({
      tooltipText: `{from}-{to}`,
      width: am5.percent(90),
      tooltipY: 0,
    });

    series.bullets.push(function (x) {
      return am5.Bullet.new(x, {
        locationY: 0.5,
        sprite: am5.Label.new(x, {
          text: '{valueY}',
          fill: x.interfaceColors.get('alternativeText'),
          centerY: am5.p50,
          centerX: am5.p50,
          populateText: true,
        }),
      });
    });

    series.data.setAll(this.transformedData);
    series.appear();

    // Make stuff animate on load
    // https://www.amcharts.com/docs/v5/concepts/animations/
    chart.appear(1000, 100);
  }

  disconnectedCallback() {
    // Clean up chart instance on component removal
    if (this.chartRoot) {
      this.chartRoot.dispose();
    }
  }
  componentDidLoad() {
    this.transformData();
  }
  render() {
    return (
      <chart-container
        adjustChartWidthForKpis
        chartId={this.chartId}
        title={this.title}
        subheader={this.subheader}
        size={this.size}
        kpis={this.kpis}
        customWidth={this.customWidth}
        customHeight={this.customHeight}
        customCardWidth={this.customCardWidth}
        customCardStyle={this.customCardStyle}
        kpisPosition={this.kpisPosition}
        hideAdornment={this.hideAdornment}
      />
    );
  }
}
